import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  faArrowUp,
  faArrowDown,
  faMinus,
  faTruck,
  faCheck,
  faClock,
  faBan,
  faChartLine,
  faMoneyBillWave,
  faCreditCard,
  faRotateLeft,
  faTriangleExclamation,
  faChartBar,
  faInbox,
  faChartPie,
  faExchangeAlt,
  faFileInvoice,
  faUsers,
  faBoxOpen,
  faCoins,
  faPlus,
  faCashRegister,
  faFileAlt,
  faCog,
  faShoppingCart,
  faBook,
  faHistory,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { modernSelectStyle } from "../../design/selectStyle";
import DateRange from "../global/DateRange";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import mainAxios from "../../services/mainAxios";
import { useAppContext, usePersistedState } from "../../AppContext";
import moment from "moment";
import func from "../../services/functions";
import AlertCertificate from "../global/AlertCertificate";
import AlertSubscription from "../global/AlertSubscription";
import CountUp from "react-countup";
import LastPurchaseInvoices from "./LastPurchaseInvoices";
import DoughnutPieTable from "./DoughnutPieTable";
import TcrCashBalance from "./TcrCashBalance";
import useInViewOnce from "../global/hooks/useInViewOnce";
import { Line } from "react-chartjs-2";
import "chart.js/auto";

// Skeleton Components
const SkeletonMetric = () => (
  <div className="db-skeleton-metric">
    <div className="db-skeleton-metric__icon" />
    <div className="db-skeleton-metric__content">
      <div className="db-skeleton-metric__label" />
      <div className="db-skeleton-metric__value" />
      <div className="db-skeleton-metric__count" />
    </div>
  </div>
);

const SkeletonCard = ({ rows = 4 }) => (
  <div className="db-skeleton-card">
    <div className="db-skeleton-card__header" />
    {[...Array(rows)].map((_, i) => (
      <div key={i} className="db-skeleton-card__row">
        <div className="db-skeleton-card__label" />
        <div className="db-skeleton-card__value" />
      </div>
    ))}
  </div>
);

const SkeletonChart = () => (
  <div className="db-skeleton-chart">
    <div className="db-skeleton-chart__header">
      <div />
      <div />
    </div>
    <div className="db-skeleton-chart__body">
      {[65, 45, 80, 55, 70, 40, 60].map((h, i) => (
        <div key={i} className="db-skeleton-chart__bar" style={{ height: `${h}%` }} />
      ))}
    </div>
  </div>
);

const SkeletonTable = ({ rows = 5 }) => (
  <div className="db-skeleton-table">
    <div className="db-skeleton-table__header">
      <div />
    </div>
    {[...Array(rows)].map((_, i) => (
      <div key={i} className="db-skeleton-table__row">
        <div className="db-skeleton-table__rank" />
        <div className="db-skeleton-table__name" />
        <div className="db-skeleton-table__value" />
      </div>
    ))}
  </div>
);

// Empty State Component
const EmptyState = ({ icon, title, text, compact = false, inline = false }) => (
  <div className={`db-empty ${compact ? "db-empty--compact" : ""} ${inline ? "db-empty--inline" : ""}`}>
    <div className="db-empty__icon">
      <FontAwesomeIcon icon={icon} />
    </div>
    {inline ? (
      <div className="db-empty__content">
        <div className="db-empty__title">{title}</div>
        {text && <div className="db-empty__text">{text}</div>}
      </div>
    ) : (
      <>
        <div className="db-empty__title">{title}</div>
        {text && <div className="db-empty__text">{text}</div>}
      </>
    )}
  </div>
);

// Exchange Rate Card Component
const ExchangeRateCard = ({ rates, loading, t }) => {
  // Currency config with country codes for flag images
  const currencyConfig = {
    EUR: { name: "Euro", country: "eu" },
    USD: { name: "US Dollar", country: "us" },
    GBP: { name: "British Pound", country: "gb" },
    CHF: { name: "Swiss Franc", country: "ch" },
  };
  const importantCurrencies = ["EUR", "USD", "GBP", "CHF"];
  const filteredRates = rates.filter(rate => importantCurrencies.includes(rate.code));

  const exportCSV = () => {
    if (!filteredRates.length) return;
    const header = "Currency,Name,Rate";
    const rows = filteredRates.map(rate => {
      const config = currencyConfig[rate.code] || {};
      return `${rate.code},${config.name || ""},${rate.value.toFixed(2)}`;
    });
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "exchange_rates.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="db-card db-exchange-card-new">
        <h3>{t("dashboard.exchangeRates") || "Exchange Rates"}</h3>
        <div className="db-exchange-list">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="db-exchange-item db-exchange-item--skeleton">
              <div className="shimmer" style={{ width: 28, height: 20, borderRadius: 4 }} />
              <div style={{ flex: 1 }}>
                <div className="shimmer" style={{ width: 60, height: 14, marginBottom: 4 }} />
                <div className="shimmer" style={{ width: 80, height: 11 }} />
              </div>
              <div className="shimmer" style={{ width: 65, height: 18 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="db-card db-exchange-card-new">
      <div className="db-exchange-card-new__header">
        <h3>{t("dashboard.exchangeRates") || "Exchange Rates"}</h3>
        {filteredRates.length > 0 && (
          <button className="db-exchange-csv-btn" onClick={exportCSV}>
            <FontAwesomeIcon icon={faDownload} />
            <span className="db-exchange-csv-btn__tooltip">{t("dashboard.downloadCsv") || "Download CSV"}</span>
          </button>
        )}
      </div>
      {filteredRates.length === 0 ? (
        <EmptyState
          icon={faCoins}
          title={t("dashboard.noData")}
          text={t("dashboard.noExchangeData") || "No exchange rate data available"}
          compact
        />
      ) : (
        <div className="db-exchange-list">
          {filteredRates.map((rate) => {
            const config = currencyConfig[rate.code] || {};
            return (
              <div key={rate.code} className="db-exchange-item">
                <img
                  src={`https://flagcdn.com/w40/${config.country}.png`}
                  srcSet={`https://flagcdn.com/w80/${config.country}.png 2x`}
                  alt={config.name}
                  className="db-exchange-item__flag"
                />
                <div className="db-exchange-item__info">
                  <span className="db-exchange-item__code">{rate.code}</span>
                  <span className="db-exchange-item__name">{config.name}</span>
                </div>
                <div className="db-exchange-item__rate">
                  <span className="db-exchange-item__symbol">{config.symbol}</span>
                  <span className="db-exchange-item__value">{rate.value.toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { t } = useTranslation("translations");
  const [currentPeriod, setCurrentPeriod] = useState({});
  const [previousPeriod, setPreviousPeriod] = useState({});
  const [purchaseStats, setPurchaseStats] = useState({});
  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [exchangeLoading, setExchangeLoading] = useState(true);
  const chartRef = useRef(null);

  const [chartRowRef, chartInView] = useInViewOnce();
  const [purchaseRef, purchaseInView] = useInViewOnce();
  const [invoicesRef, invoicesInView] = useInViewOnce();

  const {
    user: [user],
    selectedCompany: [selectedCompany],
    dates: [dates, setDates],
  } = useAppContext();

  const [hideCash, setHideCash] = usePersistedState(false, "hideCash");

  const company = user.companiesRights.find(
    (c) => c.id === parseInt(selectedCompany)
  );

  const [sessionData, setSessionData] = useState({
    fromDate: dates.fromDate,
    toDate: dates.toDate,
    businessUnitCode: null,
  });

  // Calculate previous period dates
  const getDefaultPrevPeriod = (fromDate, toDate) => {
    if (!fromDate || !toDate) return { fromDate: null, toDate: null };
    const from = moment(fromDate);
    const to = moment(toDate);
    const daysDiff = to.diff(from, "days") + 1;
    return {
      fromDate: moment(from).subtract(daysDiff, "days").format("YYYY-MM-DD"),
      toDate: moment(from).subtract(1, "days").format("YYYY-MM-DD"),
    };
  };

  const [prevPeriodDates, setPrevPeriodDates] = useState(() =>
    getDefaultPrevPeriod(dates.fromDate, dates.toDate)
  );

  // Update prev period when main period changes. Keep the same object reference
  // when the computed values are unchanged, otherwise a fresh object on mount
  // re-triggers the current+previous aggregate fetch (doubling those requests).
  useEffect(() => {
    if (sessionData.fromDate && sessionData.toDate) {
      const next = getDefaultPrevPeriod(sessionData.fromDate, sessionData.toDate);
      setPrevPeriodDates((prev) =>
        prev.fromDate === next.fromDate && prev.toDate === next.toDate ? prev : next
      );
    }
  }, [sessionData.fromDate, sessionData.toDate]);

  const userCanCreate = [1, 2, 4].includes(user.role.id);

  const businessUnitCodes = useMemo(() => {
    return [
      { label: t("invoice.businessUnitCode"), value: null },
    ].concat(
      company.branches.map((branch) => ({
        label: branch.businUnitCode + " | " + branch.sellerName,
        value: branch.businUnitCode,
      }))
    );
  }, [selectedCompany, t]);

  let buCodes = company.branches?.map((branch) => branch.businUnitCode) ?? [];

  const getFilteredResult = (fromDate, toDate) => {
    return mainAxios.post("/endpoint/v2/reports/queue/branch/aggregate", {
      object: "GetQueueCashDepositStatisticsAnalytics",
      params: { businessUnitCode: buCodes.join(",") },
      fromDate,
      toDate,
    });
  };

  const getTotals = (data) => {
    if (data?.length > 0) {
      const { id, tcrCode, businessUnitCode, actionFrom, company, purchaseUnit, ...rest } = data[0];
      let stats = {};
      Object.keys(rest).forEach((key) => {
        stats[key] = data.reduce((sum, tcrStat) => sum + tcrStat[key], 0);
      });
      return stats;
    }
    return {};
  };

  const groupedTotalsByBranch = (allStats) => {
    var result = [];
    allStats.reduce((res, curr) => {
      const { id, actionFrom, company, recordDate, ...rest } = curr;
      if (!res[curr.businessUnitCode]) {
        res[curr.businessUnitCode] = rest;
        result.push(res[curr.businessUnitCode]);
      } else {
        Object.keys(rest).forEach((key) => {
          if (typeof curr[key] === "number") {
            res[curr.businessUnitCode][key] += curr[key];
          } else {
            res[curr.businessUnitCode][key] = curr[key];
          }
        });
      }
      return res;
    }, {});
    return result;
  };

  // Fetch current period data
  useEffect(() => {
    if (!(sessionData?.fromDate && sessionData?.toDate)) return;
    if (!(prevPeriodDates?.fromDate && prevPeriodDates?.toDate)) return;
    setLoading(true);

    const fromDate = moment(sessionData.fromDate).format("YYYY-MM-DD");
    const toDate = moment(sessionData.toDate).format("YYYY-MM-DD");

    Promise.all([
      getFilteredResult(fromDate, toDate),
      getFilteredResult(prevPeriodDates.fromDate, prevPeriodDates.toDate),
    ]).then(([currentRes, prevRes]) => {
      setLoading(false);
      if (currentRes.data.status) {
        const content = currentRes.data?.content ?? [];
        setCurrentPeriod({
          totals: getTotals(content),
          byBranch: groupedTotalsByBranch(content),
          raw: content,
        });
      }
      if (prevRes.data.status) {
        const content = prevRes.data?.content ?? [];
        setPreviousPeriod({
          totals: getTotals(content),
          byBranch: groupedTotalsByBranch(content),
        });
      }
    }).catch(() => setLoading(false));
  }, [sessionData?.fromDate, sessionData?.toDate, prevPeriodDates, selectedCompany]);

  // Fetch daily stats for chart
  useEffect(() => {
    if (hideCash || !chartInView) return;
    if (!(sessionData?.fromDate && sessionData?.toDate)) return;

    const fromDate = moment(sessionData.fromDate).format("YYYY-MM-DD");
    const toDate = moment(sessionData.toDate).format("YYYY-MM-DD");

    mainAxios.post("/endpoint/v2/reports/queue/branch/analytic", {
      object: "GetQueueCashDepositStatisticsAnalytics",
      params: { buCodes: buCodes.join(",") },
      fromDate,
      toDate,
    }).then((res) => {
      if (res.data.status && res.data?.content) {
        const filtered = res.data.content.filter(
          (stat) => stat.tcrCode !== stat.businessUnitCode && stat.tcrCode !== "TS001NC001"
        );
        setDailyStats(filtered);
      }
    });
  }, [chartInView, hideCash, sessionData?.fromDate, sessionData?.toDate, selectedCompany]);

  // Fetch purchases data
  useEffect(() => {
    if (!purchaseInView) return;
    if (!(sessionData?.fromDate && sessionData?.toDate)) return;

    const fromDate = moment(sessionData.fromDate).format("YYYY-MM-DD");
    const toDate = moment(sessionData.toDate).format("YYYY-MM-DD");
    const endpoint = sessionData?.businessUnitCode
      ? "/endpoint/v2/reports/mypurchase/branch/aggregate"
      : "/endpoint/v2/reports/mypurchase/company/aggregate";

    mainAxios.post(endpoint, {
      object: "GetMyPurchaseCashDepositStatisticsAnalytics",
      fromDate,
      toDate,
      ...(sessionData?.businessUnitCode
        ? { params: { businessUnitCode: sessionData?.businessUnitCode } }
        : {}),
    }).then((res) => {
      if (res.data.status && res.data?.content) {
        setPurchaseStats(getTotals(res.data.content));
      }
    });
  }, [purchaseInView, sessionData, selectedCompany]);

  // Fetch exchange rates
  useEffect(() => {
    setExchangeLoading(true);
    mainAxios.get("api/exchange-rates/getAll")
      .then((res) => {
        if (res.data) {
          setExchangeRates(res.data);
        }
        setExchangeLoading(false);
      })
      .catch(() => {
        setExchangeLoading(false);
      });
  }, []);

  // Doughnut data is derived from the current-period results already fetched
  // above (currentPeriod.raw) — no separate /aggregate request needed.
  const todayTotalsByBranch = useMemo(() => {
    if (hideCash) return [];
    const raw = currentPeriod.raw ?? [];
    const filtered = raw.filter((stat) => stat.tcrCode !== "TS001NC001");
    return groupedTotalsByBranch(filtered);
  }, [currentPeriod, hideCash]);

  // Get data based on BU filter
  const data = useMemo(() => {
    if (sessionData?.businessUnitCode) {
      return {
        current: currentPeriod.byBranch?.find(
          (b) => b.businessUnitCode === sessionData.businessUnitCode
        ) || {},
        previous: previousPeriod.byBranch?.find(
          (b) => b.businessUnitCode === sessionData.businessUnitCode
        ) || {},
      };
    }
    return {
      current: currentPeriod.totals || {},
      previous: previousPeriod.totals || {},
    };
  }, [sessionData?.businessUnitCode, currentPeriod, previousPeriod]);

  // Calculate percentage change
  const getChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Chart data
  const chartData = useMemo(() => {
    if (!dailyStats.length) return null;

    const groupedByBU = {};
    dailyStats.forEach((stat) => {
      if (!groupedByBU[stat.businessUnitCode]) {
        groupedByBU[stat.businessUnitCode] = {};
      }
      const date = moment(stat.recordDate).format("DD MMM");
      if (!groupedByBU[stat.businessUnitCode][date]) {
        groupedByBU[stat.businessUnitCode][date] = 0;
      }
      groupedByBU[stat.businessUnitCode][date] += stat.cashTaxInclusiveAmount || 0;
    });

    const allDates = [...new Set(dailyStats.map(s => moment(s.recordDate).format("DD MMM")))];
    allDates.sort((a, b) => moment(a, "DD MMM").diff(moment(b, "DD MMM")));

    const colors = [
      { border: "#007567", bg: "rgba(0, 117, 103, 0.1)" },
      { border: "#6366f1", bg: "rgba(99, 102, 241, 0.1)" },
      { border: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)" },
      { border: "#ef4444", bg: "rgba(239, 68, 68, 0.1)" },
      { border: "#10b981", bg: "rgba(16, 185, 129, 0.1)" },
    ];

    const datasets = Object.keys(groupedByBU).slice(0, 5).map((buCode, idx) => {
      const buLabel = businessUnitCodes.find(b => b.value === buCode)?.label?.split("|")[0]?.trim() || buCode;
      return {
        label: buLabel,
        data: allDates.map(date => groupedByBU[buCode][date] || 0),
        borderColor: colors[idx % colors.length].border,
        backgroundColor: colors[idx % colors.length].bg,
        tension: 0.4,
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 6,
      };
    });

    return { labels: allDates, datasets };
  }, [dailyStats, businessUnitCodes]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 11 },
        },
      },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "#111827",
        bodyColor: "#6b7280",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        padding: 12,
        usePointStyle: true,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${func.getFormattedMoneyValue(ctx.raw)} L`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: "#9ca3af" },
      },
      y: {
        grid: { color: "#f3f4f6" },
        ticks: {
          font: { size: 11 },
          color: "#9ca3af",
          callback: (val) => func.getFormattedMoneyValue(val),
        },
      },
    },
  };

  // Calculate totals
  const totalSales = (data.current?.cashTaxInclusiveAmount ?? 0) + (data.current?.noncashTaxInclusiveAmount ?? 0);
  const prevTotalSales = (data.previous?.cashTaxInclusiveAmount ?? 0) + (data.previous?.noncashTaxInclusiveAmount ?? 0);

  // Format previous period dates for display
  const prevPeriodLabel = useMemo(() => {
    if (prevPeriodDates.fromDate && prevPeriodDates.toDate) {
      return `${moment(prevPeriodDates.fromDate).format("DD/MM")} - ${moment(prevPeriodDates.toDate).format("DD/MM")}`;
    }
    return "Select dates";
  }, [prevPeriodDates.fromDate, prevPeriodDates.toDate]);

  // Metric Card Component - tooltip only on badge
  const MetricCard = ({ label, icon, value, prevValue, count, suffix = " L", variant, showTrend = true }) => {
    const change = getChange(value, prevValue);
    const isPositive = change > 0;
    const isNeutral = change === 0;

    return (
      <div className={`db-metric ${variant ? `db-metric--${variant}` : ""}`}>
        <div className="db-metric__icon">
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="db-metric__content">
          <span className="db-metric__label">{label}</span>
          <div className="db-metric__value">
            {typeof value === "number" ? (
              <CountUp end={value} duration={0.8} separator="." decimals={suffix === " L" ? 2 : 0} decimal="," suffix={suffix} />
            ) : value}
          </div>
          {count !== undefined && (
            <span className="db-metric__count">#{count}</span>
          )}
        </div>
        {showTrend && prevValue !== undefined && (
          <div className={`db-metric__trend ${isPositive ? "db-metric__trend--up" : isNeutral ? "db-metric__trend--neutral" : "db-metric__trend--down"}`}>
            <FontAwesomeIcon icon={isPositive ? faArrowUp : isNeutral ? faMinus : faArrowDown} />
            <span>{Math.abs(change).toFixed(1)}%</span>
            {/* Tooltip only on percentage badge */}
            <div className="db-metric__tooltip">
              <div className="db-metric__tooltip-title">{t("dashboard.periodComparison")}</div>
              <div className="db-metric__tooltip-row">
                <span>{t("dashboard.current")}</span>
                <strong>{func.getFormattedMoneyValue(value)}{suffix}</strong>
              </div>
              <div className="db-metric__tooltip-row">
                <span>{t("dashboard.previous")}</span>
                <span>{func.getFormattedMoneyValue(prevValue || 0)}{suffix}</span>
              </div>
              <div className="db-metric__tooltip-row db-metric__tooltip-row--diff">
                <span>{t("dashboard.difference")}</span>
                <span className={isPositive ? "text-success" : isNeutral ? "" : "text-danger"}>
                  {isPositive ? "+" : ""}{func.getFormattedMoneyValue(value - (prevValue || 0))}{suffix}
                </span>
              </div>
              {prevPeriodDates.fromDate && prevPeriodDates.toDate && (
                <div className="db-metric__tooltip-period">
                  {moment(prevPeriodDates.fromDate).format("DD/MM")} - {moment(prevPeriodDates.toDate).format("DD/MM/YY")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="db">
      {/* Header */}
      <div className="db-header">
        <div className="db-header__left">
          <h1 className="db-header__title">{t("navbar.dashboard")}</h1>
          <AlertCertificate />
          <AlertSubscription />
        </div>
        <div className="db-header__right">
          <div className="db-hide-cash">
            <input
              type="checkbox"
              id="hideCash"
              checked={hideCash}
              onChange={(e) => setHideCash(e.target.checked)}
            />
            <label htmlFor="hideCash">{t("dashboard.hideCash")}</label>
          </div>
          <DateRange
            fromDate={sessionData.fromDate}
            toDate={sessionData.toDate}
            onChange={(dates) => {
              setSessionData({ ...sessionData, fromDate: dates[0], toDate: dates[1] });
              setDates({ fromDate: dates[0], toDate: dates[1] });
            }}
          />
          <div className="db-select-wrapper">
            <Select
              styles={modernSelectStyle}
              className="db-select"
              options={businessUnitCodes}
              value={businessUnitCodes.find((bu) => bu.value === sessionData?.businessUnitCode) || businessUnitCodes[0]}
              isSearchable={true}
              onChange={(bu) => setSessionData({ ...sessionData, businessUnitCode: bu?.value })}
            />
          </div>
        </div>
      </div>

      {/* Main Stats Row */}
      <div className="db-stats-row">
        {loading ? (
          <>
            <SkeletonMetric />
            <SkeletonMetric />
            <SkeletonMetric />
            <SkeletonMetric />
            <SkeletonMetric />
          </>
        ) : (
          <>
            <MetricCard
              label={t("dashboard.totalSales")}
              icon={faChartLine}
              value={totalSales}
              prevValue={prevTotalSales}
              count={data.current?.successInvoicesNo ?? 0}
              variant="primary"
            />
            <MetricCard
              label={t("dashboard.cash")}
              icon={faMoneyBillWave}
              value={data.current?.cashTaxInclusiveAmount ?? 0}
              prevValue={data.previous?.cashTaxInclusiveAmount ?? 0}
              count={data.current?.cashInvoicesNo ?? 0}
              variant="teal"
            />
            <MetricCard
              label={t("dashboard.nonCash")}
              icon={faCreditCard}
              value={data.current?.noncashTaxInclusiveAmount ?? 0}
              prevValue={data.previous?.noncashTaxInclusiveAmount ?? 0}
              count={data.current?.noncashInvoicesNo ?? 0}
              variant="purple"
            />
            <MetricCard
              label={t("invoice.corrective")}
              icon={faRotateLeft}
              value={data.current?.correctiveTotalWithVat ?? 0}
              prevValue={data.previous?.correctiveTotalWithVat ?? 0}
              count={(data.current?.cashCorrectiveNo ?? 0) + (data.current?.noncashCorrectiveNo ?? 0)}
              variant="warning"
            />
            <MetricCard
              label={t("dashboard.errors")}
              icon={faTriangleExclamation}
              value={data.current?.errorInvoicesNo ?? 0}
              prevValue={data.previous?.errorInvoicesNo ?? 0}
              suffix=""
              variant="danger"
              showTrend={false}
            />
          </>
        )}
      </div>

      {/* Chart + Quick Actions Row */}
      {!hideCash && (
        <div className="db-chart-row" ref={chartRowRef}>
          {loading ? (
            <SkeletonChart />
          ) : chartData && chartData.datasets.length > 0 ? (
            <div className="db-chart-card">
              <div className="db-chart-card__header">
                <h3>{t("dashboard.cashSalesTrend")}</h3>
              </div>
              <div className="db-chart-card__body">
                <Line ref={chartRef} data={chartData} options={chartOptions} />
              </div>
            </div>
          ) : (
            <div className="db-chart-card">
              <div className="db-chart-card__header">
                <h3>{t("dashboard.cashSalesTrend")}</h3>
              </div>
              <div className="db-chart-card__body">
                <EmptyState
                  icon={faChartBar}
                  title={t("dashboard.noData")}
                  text={t("dashboard.noChartData") || "No sales data available for this period"}
                />
              </div>
            </div>
          )}
          {/* Quick Actions Card */}
          <div className="db-card db-quick-actions">
            <h3>{t("dashboard.quickActions") || "Veprime të Shpejta"}</h3>
            <div className="db-quick-actions__grid">
              <Link to="/my-invoices" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--primary">
                  <FontAwesomeIcon icon={faFileInvoice} />
                </div>
                <span className="db-quick-action__label">{t("navbar.myInvoices") || "Faturat"}</span>
              </Link>
              <Link to="/books/sales" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--blue">
                  <FontAwesomeIcon icon={faBook} />
                </div>
                <span className="db-quick-action__label">{t("navbar.salesBook") || "Libri i Shitjeve"}</span>
              </Link>
              <Link to="/cash-register" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--warning">
                  <FontAwesomeIcon icon={faCashRegister} />
                </div>
                <span className="db-quick-action__label">{t("navbar.cashRegister") || "Arka"}</span>
              </Link>
              <Link to="/reports" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--purple">
                  <FontAwesomeIcon icon={faFileAlt} />
                </div>
                <span className="db-quick-action__label">{t("navbar.reports") || "Raporte"}</span>
              </Link>
              <Link to="/logs" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--teal">
                  <FontAwesomeIcon icon={faHistory} />
                </div>
                <span className="db-quick-action__label">{t("navbar.logs") || "Historiku"}</span>
              </Link>
              <Link to="/settings/preferences" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--gray">
                  <FontAwesomeIcon icon={faCog} />
                </div>
                <span className="db-quick-action__label">{t("settings.preferences") || "Preferencat"}</span>
              </Link>
            </div>
          </div>
        </div>
      )}


      {/* Content Grid - 3 Column Layout */}
      <div className="db-content-grid">
        {/* Quick Actions Card - shown here when cash is hidden */}
        {hideCash && (
          <div className="db-card db-quick-actions">
            <h3>{t("dashboard.quickActions") || "Veprime të Shpejta"}</h3>
            <div className="db-quick-actions__grid">
              <Link to="/my-invoices" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--primary">
                  <FontAwesomeIcon icon={faFileInvoice} />
                </div>
                <span className="db-quick-action__label">{t("navbar.myInvoices") || "Faturat"}</span>
              </Link>
              <Link to="/books/sales" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--blue">
                  <FontAwesomeIcon icon={faBook} />
                </div>
                <span className="db-quick-action__label">{t("navbar.salesBook") || "Libri i Shitjeve"}</span>
              </Link>
              <Link to="/cash-register" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--warning">
                  <FontAwesomeIcon icon={faCashRegister} />
                </div>
                <span className="db-quick-action__label">{t("navbar.cashRegister") || "Arka"}</span>
              </Link>
              <Link to="/reports" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--purple">
                  <FontAwesomeIcon icon={faFileAlt} />
                </div>
                <span className="db-quick-action__label">{t("navbar.reports") || "Raporte"}</span>
              </Link>
              <Link to="/logs" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--teal">
                  <FontAwesomeIcon icon={faHistory} />
                </div>
                <span className="db-quick-action__label">{t("navbar.logs") || "Historiku"}</span>
              </Link>
              <Link to="/settings/preferences" className="db-quick-action">
                <div className="db-quick-action__icon db-quick-action__icon--gray">
                  <FontAwesomeIcon icon={faCog} />
                </div>
                <span className="db-quick-action__label">{t("settings.preferences") || "Preferencat"}</span>
              </Link>
            </div>
          </div>
        )}

        {/* Period Comparison Card */}
        {loading ? (
          <div className="db-card db-comparison-card">
            <div className="db-comparison-card__header">
              <div className="shimmer" style={{ width: "50%", height: 16 }} />
              <div className="shimmer" style={{ width: "70%", height: 14, marginTop: 8 }} />
            </div>
            <div className="db-comparison-table">
              <div className="db-comparison-table__header">
                <span></span>
                <div className="shimmer" style={{ width: 50, height: 12 }} />
                <div className="shimmer" style={{ width: 50, height: 12 }} />
                <div className="shimmer" style={{ width: 30, height: 12 }} />
              </div>
              {[...Array(4)].map((_, i) => (
                <div key={i} className="db-comparison-table__row">
                  <div className="shimmer" style={{ width: "40%", height: 14 }} />
                  <div className="shimmer" style={{ width: 60, height: 14 }} />
                  <div className="shimmer" style={{ width: 60, height: 14 }} />
                  <div className="shimmer" style={{ width: 40, height: 14 }} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="db-card db-comparison-card">
            <div className="db-comparison-card__header">
              <h3>{t("dashboard.periodComparison")}</h3>
              <div className="db-comparison-card__periods">
                <span className="db-comparison-card__current">
                  {moment(sessionData.fromDate).format("DD/MM")} - {moment(sessionData.toDate).format("DD/MM")}
                </span>
                <span className="db-comparison-card__vs">vs</span>
                <div className="db-comparison-card__prev-picker">
                  <DateRange
                    fromDate={prevPeriodDates.fromDate ? new Date(prevPeriodDates.fromDate) : null}
                    toDate={prevPeriodDates.toDate ? new Date(prevPeriodDates.toDate) : null}
                    onChange={(dates) => {
                      setPrevPeriodDates({
                        fromDate: dates[0] ? moment(dates[0]).format("YYYY-MM-DD") : null,
                        toDate: dates[1] ? moment(dates[1]).format("YYYY-MM-DD") : null
                      });
                    }}
                    showDateMenu={false}
                  />
                </div>
              </div>
            </div>
            <div className="db-comparison-table">
              <div className="db-comparison-table__header">
                <span></span>
                <span>{t("dashboard.current")}</span>
                <span>{t("dashboard.previous")}</span>
                <span>%</span>
              </div>
              {[
                { label: t("dashboard.invoices"), current: data.current?.successInvoicesNo ?? 0, prev: data.previous?.successInvoicesNo ?? 0, isMoney: false },
                { label: t("dashboard.cash"), current: data.current?.cashTaxInclusiveAmount ?? 0, prev: data.previous?.cashTaxInclusiveAmount ?? 0, isMoney: true },
                { label: t("dashboard.nonCash"), current: data.current?.noncashTaxInclusiveAmount ?? 0, prev: data.previous?.noncashTaxInclusiveAmount ?? 0, isMoney: true },
              ].map((item, idx) => {
                const change = getChange(item.current, item.prev);
                return (
                  <div key={idx} className="db-comparison-table__row">
                    <span className="db-comparison-table__label">{item.label}</span>
                    <span className="db-comparison-table__current">
                      {item.isMoney ? func.getFormattedMoneyValue(item.current) : item.current}
                    </span>
                    <span className="db-comparison-table__prev">
                      {item.isMoney ? func.getFormattedMoneyValue(item.prev) : item.prev}
                    </span>
                    <span className={`db-comparison-table__change ${change > 0 ? "text-success" : change < 0 ? "text-danger" : ""}`}>
                      {change > 0 ? "+" : ""}{change.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
              <div className="db-comparison-table__row db-comparison-table__row--total">
                <span className="db-comparison-table__label">Total</span>
                <span className="db-comparison-table__current">{func.getFormattedMoneyValue(totalSales)}</span>
                <span className="db-comparison-table__prev">{func.getFormattedMoneyValue(prevTotalSales)}</span>
                <span className={`db-comparison-table__change ${getChange(totalSales, prevTotalSales) > 0 ? "text-success" : getChange(totalSales, prevTotalSales) < 0 ? "text-danger" : ""}`}>
                  {getChange(totalSales, prevTotalSales) > 0 ? "+" : ""}{getChange(totalSales, prevTotalSales).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Business Units Cash Balance (Doughnut) OR Sales Card when hidden */}
        {!hideCash ? (
          loading ? (
            <div className="db-card db-doughnut-skeleton">
              <h3>{t("dashboard.buCashBalance")}</h3>
              <div className="db-doughnut-skeleton__chart">
                <div className="shimmer db-doughnut-skeleton__circle" />
              </div>
            </div>
          ) : (
            <div className="db-doughnut-wrapper">
              <DoughnutPieTable
                title={t("dashboard.buCashBalance")}
                todayTotalsByBranch={todayTotalsByBranch}
                businessUnitCodes={businessUnitCodes}
              />
            </div>
          )
        ) : (
          // Show Sales card here when cash is hidden
          loading ? (
            <SkeletonCard rows={3} />
          ) : (
            <div className="db-card db-sales-card-new">
              <div className="db-sales-header">
                <h3>{t("dashboard.salesAndPurchase")}</h3>
                <span className="db-sales-count">#{data.current?.successInvoicesNo ?? 0} {t("dashboard.invoicesShort")}</span>
              </div>
              <div className="db-sales-total">
                <span className="db-sales-total__value">{func.getFormattedMoneyValue(totalSales)}</span>
                <span className="db-sales-total__currency">ALL</span>
              </div>
              <div className="db-sales-grid">
                <div className="db-sales-tile db-sales-tile--cash">
                  <div className="db-sales-tile__icon">
                    <FontAwesomeIcon icon={faMoneyBillWave} />
                  </div>
                  <div className="db-sales-tile__content">
                    <span className="db-sales-tile__value">{func.getFormattedMoneyValue(data.current?.cashTaxInclusiveAmount ?? 0)} L</span>
                    <span className="db-sales-tile__label">{t("dashboard.cash")}</span>
                  </div>
                  <span className="db-sales-tile__percent">{totalSales > 0 ? ((data.current?.cashTaxInclusiveAmount ?? 0) / totalSales * 100).toFixed(0) : 0}%</span>
                </div>
                <div className="db-sales-tile db-sales-tile--noncash">
                  <div className="db-sales-tile__icon">
                    <FontAwesomeIcon icon={faCreditCard} />
                  </div>
                  <div className="db-sales-tile__content">
                    <span className="db-sales-tile__value">{func.getFormattedMoneyValue(data.current?.noncashTaxInclusiveAmount ?? 0)} L</span>
                    <span className="db-sales-tile__label">{t("dashboard.nonCash")}</span>
                  </div>
                  <span className="db-sales-tile__percent">{totalSales > 0 ? ((data.current?.noncashTaxInclusiveAmount ?? 0) / totalSales * 100).toFixed(0) : 0}%</span>
                </div>
              </div>
            </div>
          )
        )}

        {/* Exchange Rates Card */}
        <ExchangeRateCard rates={exchangeRates} loading={exchangeLoading} t={t} />

        {/* Last Purchase Invoices - Spans 2 columns */}
        <div className="db-invoices-wrapper" ref={invoicesRef}>
          <LastPurchaseInvoices inView={invoicesInView} />
        </div>

        {/* Purchase Statistics */}
        {loading ? (
          <SkeletonCard rows={4} />
        ) : (
          <div className="db-card db-purchase-card" ref={purchaseRef}>
            <h3>{t("dashboard.purchaseDashboard")}</h3>
            <div className="db-status-grid">
              <div className="db-status-item">
                <div className="db-status-item__icon db-status-item__icon--warning">
                  <FontAwesomeIcon icon={faTruck} />
                </div>
                <div className="db-status-item__content">
                  <span>{func.capitalizeFirstLetter(t("status.DELIVERED"))}</span>
                </div>
                <div className="db-status-item__value">{purchaseStats?.cisDeliveredStatusInvoices ?? 0}</div>
              </div>
              <div className="db-status-item">
                <div className="db-status-item__icon db-status-item__icon--primary">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div className="db-status-item__content">
                  <span>{func.capitalizeFirstLetter(t("status.PARTIALLY_PAID"))}</span>
                </div>
                <div className="db-status-item__value">{purchaseStats?.cisPartiallyPaidStatusInvoices ?? 0}</div>
              </div>
              <div className="db-status-item">
                <div className="db-status-item__icon db-status-item__icon--success">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <div className="db-status-item__content">
                  <span>{func.capitalizeFirstLetter(t("status.PAID"))}</span>
                </div>
                <div className="db-status-item__value">{purchaseStats?.cisPaidStatusInvoices ?? 0}</div>
              </div>
              <div className="db-status-item">
                <div className="db-status-item__icon db-status-item__icon--danger">
                  <FontAwesomeIcon icon={faBan} />
                </div>
                <div className="db-status-item__content">
                  <span>{t("purchases.rejected")}</span>
                </div>
                <div className="db-status-item__value">{purchaseStats?.cisRejectedStatusInvoices ?? 0}</div>
              </div>
            </div>
          </div>
        )}

        {/* TCR Cash Balance */}
        {!hideCash && (
          <TcrCashBalance
            title={t("dashboard.tcrCashBalance")}
            stats={currentPeriod.raw ?? []}
            sessionData={sessionData}
            loading={loading}
          />
        )}

        {/* Sales Overview Card - Only show when cash is not hidden */}
        {!hideCash && (
          loading ? (
            <SkeletonCard rows={3} />
          ) : (
            <div className="db-card db-sales-card-new">
              <div className="db-sales-header">
                <h3>{t("dashboard.salesAndPurchase")}</h3>
                <span className="db-sales-count">#{data.current?.successInvoicesNo ?? 0} {t("dashboard.invoicesShort")}</span>
              </div>
              <div className="db-sales-total">
                <span className="db-sales-total__value">{func.getFormattedMoneyValue(totalSales)}</span>
                <span className="db-sales-total__currency">ALL</span>
              </div>
              <div className="db-sales-grid">
                <div className="db-sales-tile db-sales-tile--cash">
                  <div className="db-sales-tile__icon">
                    <FontAwesomeIcon icon={faMoneyBillWave} />
                  </div>
                  <div className="db-sales-tile__content">
                    <span className="db-sales-tile__value">{func.getFormattedMoneyValue(data.current?.cashTaxInclusiveAmount ?? 0)} L</span>
                    <span className="db-sales-tile__label">{t("dashboard.cash")}</span>
                  </div>
                  <span className="db-sales-tile__percent">{totalSales > 0 ? ((data.current?.cashTaxInclusiveAmount ?? 0) / totalSales * 100).toFixed(0) : 0}%</span>
                </div>
                <div className="db-sales-tile db-sales-tile--noncash">
                  <div className="db-sales-tile__icon">
                    <FontAwesomeIcon icon={faCreditCard} />
                  </div>
                  <div className="db-sales-tile__content">
                    <span className="db-sales-tile__value">{func.getFormattedMoneyValue(data.current?.noncashTaxInclusiveAmount ?? 0)} L</span>
                    <span className="db-sales-tile__label">{t("dashboard.nonCash")}</span>
                  </div>
                  <span className="db-sales-tile__percent">{totalSales > 0 ? ((data.current?.noncashTaxInclusiveAmount ?? 0) / totalSales * 100).toFixed(0) : 0}%</span>
                </div>
              </div>
            </div>
          )
        )}

        {/* Cash Movements */}
        {!hideCash && (
          loading ? (
            <SkeletonCard rows={4} />
          ) : (
            <div className="db-card">
              <h3>{t("dashboard.cashMovements")}</h3>
              {!data.current || Object.keys(data.current).length === 0 ? (
                <EmptyState
                  icon={faExchangeAlt}
                  title={t("dashboard.noData")}
                  text={t("dashboard.noCashMovements") || "No cash movements recorded"}
                  compact
                />
              ) : (
                <div className="db-mini-stats">
                  <div className="db-mini-stat">
                    <span className="db-mini-stat__label">{t("dashboard.initialBalance")}</span>
                    <span className="db-mini-stat__value">{func.getFormattedMoneyValue(data.current?.cashInit ?? 0)} L</span>
                  </div>
                  <div className="db-mini-stat">
                    <span className="db-mini-stat__label">{t("cashRegister.deposit")}</span>
                    <span className="db-mini-stat__value text-success">+{func.getFormattedMoneyValue(data.current?.cashDeposit ?? 0)} L</span>
                  </div>
                  <div className="db-mini-stat">
                    <span className="db-mini-stat__label">{t("dashboard.withdrawDash")}</span>
                    <span className="db-mini-stat__value text-warning">-{func.getFormattedMoneyValue(data.current?.cashWithdraw ?? 0)} L</span>
                  </div>
                  <div className="db-mini-stat db-mini-stat--total">
                    <span className="db-mini-stat__label">{t("dashboard.cashBalance")}</span>
                    <span className="db-mini-stat__value">{func.getFormattedMoneyValue((data.current?.cashEnd ?? 0) + (data.current?.banknoteCashTot ?? 0))} L</span>
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Dashboard;
